import { Box, Button, Center, chakra, Heading, Text } from '@chakra-ui/react'
import { useRouter } from 'next/router'
import pMap from 'p-map'
import { useContext, useMemo, useState } from 'react'

import assertDefined from '@/utils/assertDefined'

import { type ResearchGroup, SeriesWithdrawalSource } from '@/queries/constants'
import ProgramConsentQ from '@/queries/ProgramConsentQ'
import UserQ from '@/queries/UserQ'
import WorldQ from '@/queries/WorldQ'
import { useQuery } from '@/client/context/query'
import { useUser } from '@/client/context/user'
import useAPIWrapper from '@/client/hooks/useAPIWrapper'
import RPC, { RPCRefresh } from '@/client/rpc'
import isUserAgeAssured from '@/client/services/ageAssurance/isUserAgeAssured'
import {
  getRequestParentConsentFormInputs,
  getRequestParentConsentOnValidate,
} from '@/client/services/formInputs/getRequestParentConsentFormInputs'
import { getAlgebra2FinalsPrepProgramStructureConsentFormInputs } from '@/client/services/formInputs/learningLabsFormInputs/getAlgebra2FinalsPrepProgramStructureConsent'
import {
  ALGEBRA2_LEARNING_LABS_PROGRAM_SLUG,
  COLLEGE_ADMISSIONS_WORKSHOPS_PROGRAM_SLUG,
  PRE_CALCULUS_LEARNING_LABS_PROGRAM_SLUG,
  type ProgramSlug,
  SAT_BOOTCAMP_PROGRAM_SLUG,
} from '@/client/services/programs/constants'
import { needsConsentForProgram } from '@/client/services/programs/helpers'
import { ALGEBRA2_FINAL_EXAM_PREP_SPRING_2025_PREFIX } from '@/client/services/programs/ka-learning-labs/algebra2/constants'
import { getRequestParentConsentUrl } from '@/client/services/url'
import { hasUserRole } from '@/client/services/user'
import { safeRouterPush } from '@/client/utils'

import AgeAssuranceGuide from '@/components/AgeAssurance/AgeAssuranceGuide'
import useEditPhoneNumberModal from '@/components/Profile/useEditPhoneNumberModal'
import RegistrationModalTutorPrompt from '@/components/Registration/RegistrationModalTutorPrompt'
import useSATBootcampTestsBoundary from '@/components/SATBootcamp/hooks/useSATBootcampTestsBoundary'
import ShwLink from '@/components/ShwLink'
import { useToaster } from '@/components/Toast/useToaster'
import { FormContext, type FormInputType, type FormValues } from '@/components/UI/Form/types'
import type { FormWizardV2Step, FormWizardV2Values } from '@/components/UI/FormWizardV2/types'

import { trackEvent } from '../analytics'
import { getSeriesReservationTimeHours } from '../series'
import {
  ALGEBRA2_LEARNING_LABS_WORLD_SLUG,
  COLLEGE_ADMISSIONS_WORKSHOPS_WORLD_SLUG,
  PRE_CALCULUS_LEARNING_LABS_WORLD_SLUG,
} from '../worlds'
import { getCAWConsentFormInputs, getCAWConsentResponses } from './cawConsentFormInputs'
import { getAssentFormInputs } from './getAssentFormInputs'
import getSeriesRegistrationFormBlocks, {
  getSeriesRegistrationOnValidate,
} from './getSeriesRegistrationFormBlocks'
import {
  getLearningLabsConsentFormInputs,
  getLearningLabsConsentResponses,
} from './learningLabsFormInputs'
import { getSATBootcampLearnerFormInputs } from './satBootcamp/getSATBootcampLearnerFormInputs'

// We wrap AgeAssuranceGuide in a local component in order
// to have access to the onSubmit function for the age assurance
// step the series registration wizard.
function AgeAssuranceGuideWithAutoSubmit({
  onSessionModalDone,
  onAgeAssured,
}: {
  onSessionModalDone: () => void
  onAgeAssured: () => void
}) {
  const { onSubmit } = useContext(FormContext)
  return (
    <AgeAssuranceGuide
      showVerifyLaterNotice
      onAgeAssured={onAgeAssured}
      onSessionModalDone={result => {
        onSessionModalDone()
        // Auto-submit the series registration wizard if the user
        // chose "Verify Later"
        if (result === 'later') {
          onSubmit()
        }
      }}
    />
  )
}

const CAW_CONSENT_SLUG = 'caw-student-consent'
const ALGEBRA2_LEARNING_LABS_CONSENT_SLUG = 'algebra2-learning-labs-learner-consent'
const PRE_CALCULUS_LEARNING_LABS_CONSENT_SLUG = 'pre-calculus-learning-labs-learner-consent'
const ALGEBRA2_FINALS_PREP_PROGRAM_STRUCTURE_CONSENT_SLUG = 'algebra2-finals-prep-program-structure'

export type SeriesRegistrationWizardStepNames =
  | 'attendance-policy-and-questionnaire'
  | 'did-you-know'
  | 'phone-number'
  | 'request-parent-consent'
  | 'waiting-for-parent-consent'
  | 'request-student-assent'
  | 'request-parent-contact'
  | typeof CAW_CONSENT_SLUG
  | typeof ALGEBRA2_LEARNING_LABS_CONSENT_SLUG
  | typeof PRE_CALCULUS_LEARNING_LABS_CONSENT_SLUG
  | typeof ALGEBRA2_FINALS_PREP_PROGRAM_STRUCTURE_CONSENT_SLUG
  | 'sat-bootcamp-questions'
  | 'age-assurance'
  | 'review'

export default function useSeriesRegistrationWizardSteps({
  // Begin Series Data
  seriesId,
  attendancePolicy,
  seriesQuestionnaire,
  hasRequiredAttendanceSessions,
  isSATBootcampCourse,
  programSlug,
  tutorInfo,
  templateSlug,
  // End Series Data
  onClose,
  redirectUrl,
  reloadAfterRedirect = false,
  requirePhoneNumber = false,
  HeaderComponent = () => null,
  source,
  seriesIdsToUnregister = [],
  seriesIdsToUnreserve = [],
  switchReason = null,
}: {
  seriesId: number
  attendancePolicy: string
  seriesQuestionnaire: FormInputType[] | null
  hasRequiredAttendanceSessions: boolean
  isSATBootcampCourse: boolean
  programSlug?: ProgramSlug | 'dialogues'
  tutorInfo?: {
    name: string
    avatarUrl: string
  } | null
  spotsAvailableToReserve: number
  countUserReservationsInSeries: number
  onClose?: () => void
  redirectUrl: string
  reloadAfterRedirect?: boolean
  requirePhoneNumber?: boolean
  HeaderComponent: () => JSX.Element | null
  source: string
  researchGroup: ResearchGroup | null
  seriesIdsToUnregister?: number[]
  seriesIdsToUnreserve?: number[]
  templateSlug?: string | null
  switchReason?: string | null
}): {
  content: JSX.Element
  handleSubmit: (values: FormWizardV2Values<SeriesRegistrationWizardStepNames>) => Promise<void>
  steps: FormWizardV2Step<SeriesRegistrationWizardStepNames>[]
  blockedError: string | null
} {
  const currentUser = useUser()
  const query = useQuery()
  const toast = useToaster()
  const router = useRouter()

  const [blockedError, setBlockedError] = useState<string | null>(null)

  const me = query.one(UserQ.where({ id: currentUser.id }))
  const hasPhoneNumber = me<boolean>`${UserQ.phone_number} IS NOT NULL` ?? false
  const hasPhoneNumberWaived = me<boolean>`${UserQ.phone_number_waived_at} IS NOT NULL` ?? false

  const parentConsentRequestedAt = me(UserQ.parent_consent_requested_at)
  const parentConsentCompletedAt = me(UserQ.parent_consent_completed_at)
  const parentConsentWaivedAt = me(UserQ.parent_consent_waived_at)
  const userParentConsentSatisfied =
    parentConsentCompletedAt != null || parentConsentWaivedAt != null
  const userAssentCompletedAt = me(UserQ.assent_completed_at)
  const parentDetails = me(UserQ.parent_details)
  const userHasParentContactInfo = parentDetails != null
  const parentEmail = parentDetails?.email
  const testsBoundary = useSATBootcampTestsBoundary()

  const consentsCompleted = query.many(
    ProgramConsentQ.where({
      userId: currentUser.id,
      type: 'learner',
      include: [
        {
          association: 'World',
          required: true,
        },
      ],
    })
  )

  const learnerCompletedConsentSubworldSlugs = consentsCompleted.map(consent =>
    consent(WorldQ.slug)
  )

  const completedCAWLearnerConsent = learnerCompletedConsentSubworldSlugs.includes(
    COLLEGE_ADMISSIONS_WORKSHOPS_WORLD_SLUG
  )
  const completedAlgebra2LearningLabsLearnerConsent = learnerCompletedConsentSubworldSlugs.includes(
    ALGEBRA2_LEARNING_LABS_WORLD_SLUG
  )
  const completedPreCalculusLearningLabsLearnerConsent =
    learnerCompletedConsentSubworldSlugs.includes(PRE_CALCULUS_LEARNING_LABS_WORLD_SLUG)

  const reservationTimeHours = getSeriesReservationTimeHours({ userId: currentUser.id })

  const [content, showPhoneNumberModal] = useEditPhoneNumberModal({
    type: 'add',
  })

  const [isAgeAssuranceComplete, setIsAgeAssuranceComplete] = useState(false)

  const hideAgeAssurance =
    hasUserRole(currentUser, 'user:age_assurance:failed') || isUserAgeAssured(currentUser)

  const [shouldSubmit, setShouldSubmit] = useState(hideAgeAssurance)

  // Check if this program requires parent consent or assent
  const { needsParentConsent: programNeedsParentConsent, needsAssent: programNeedsAssent } =
    needsConsentForProgram(programSlug ?? '')

  const allSteps: (FormWizardV2Step<SeriesRegistrationWizardStepNames> & {
    hide?: () => boolean
  })[] = useMemo(
    () => [
      {
        slug: 'attendance-policy-and-questionnaire',
        title: 'Registration Questions',
        formInputs: getSeriesRegistrationFormBlocks({
          includeSmsUpsell: !hasPhoneNumber && !hasPhoneNumberWaived,
          attendancePolicy,
          seriesQuestionnaire,
          worldTitleToRegisterFor: null,
          hasRequiredAttendanceSessions,
          isSATBootcamp: isSATBootcampCourse,
        }),
        onValidate: getSeriesRegistrationOnValidate(),
        ContainerComponent({ title, children }) {
          return (
            <>
              <HeaderComponent />
              <Heading size="xl" mb="32px">
                {title}
              </Heading>
              <Box width="100%">
                {isSATBootcampCourse || tutorInfo == null ? null : (
                  <RegistrationModalTutorPrompt
                    tutorName={tutorInfo.name}
                    tutorAvatarUrl={tutorInfo.avatarUrl}
                    tutorProgressionLevel={null}
                  />
                )}
              </Box>
              <Box pb="24px">{children}</Box>
            </>
          )
        },
      },
      {
        slug: 'phone-number',
        title: 'Enter your phone number',
        hide: () => !requirePhoneNumber || hasPhoneNumber,
        formInputs: [
          {
            label: 'Please enter your phone number to get text reminders for sessions.',
            type: 'phone',
            name: 'phoneNumber',
            required: false,
            size: 6,
          },
        ],
        ContainerComponent({ title, children }) {
          return (
            <>
              <HeaderComponent />
              <Heading size="xl" mb="32px">
                {title}
              </Heading>
              {children}
            </>
          )
        },
      },
      {
        slug: 'request-parent-contact',
        title: 'Parent contact information',
        hide: () => {
          if (
            !isSATBootcampCourse ||
            programNeedsParentConsent ||
            userParentConsentSatisfied ||
            userHasParentContactInfo
          ) {
            return true
          }
          return false
        },
        formInputs: getRequestParentConsentFormInputs(),
        onValidate: getRequestParentConsentOnValidate({ userEmail: currentUser.email }),
        ContainerComponent({ title: _, children }) {
          return (
            <>
              <HeaderComponent />
              <Heading size="xl" mb="32px">
                Please provide your parent or guardian&apos;s contact info below.
              </Heading>
              <Text mb="24px">
                We ask for this so we can share key updates and help them support you throughout the
                program.
              </Text>
              {children}
            </>
          )
        },
      },

      {
        slug: 'request-student-assent',
        title: 'Give Permission',
        hide: () => !(programNeedsAssent && userAssentCompletedAt == null),
        formInputs: getAssentFormInputs(),
        ContainerComponent({ title, children }) {
          return (
            <>
              <HeaderComponent />
              <Heading size="xl" as="h4" mb="32px">
                {title}
              </Heading>
              {children}
            </>
          )
        },
      },
      {
        slug: 'request-parent-consent',
        title: 'Request parent consent',
        hide: () => {
          if (!programNeedsParentConsent) {
            return true
          }
          // Default logic
          return parentConsentRequestedAt != null || userParentConsentSatisfied
        },
        formInputs: getRequestParentConsentFormInputs(),
        onValidate: getRequestParentConsentOnValidate({ userEmail: currentUser.email }),
        ContainerComponent({ title: _, children }) {
          return (
            <>
              <HeaderComponent />
              <Heading size="xl" mb="32px">
                Secure your registration for the next {reservationTimeHours} hours
              </Heading>
              <Text mb="24px">
                We require parental consent in order to participate in the SAT Bootcamp program.
                Please input your parent or guardian&apos;s contact information and we&apos;ll send
                them a form.{' '}
                <chakra.span fontWeight="bold" color="fg.error">
                  If your parent does not complete this form in the next {reservationTimeHours}{' '}
                  hours, your registration will expire.
                </chakra.span>
              </Text>
              {children}
            </>
          )
        },
      },
      {
        slug: 'waiting-for-parent-consent',
        title: 'Parent consent warning',
        hide: () => {
          if (!programNeedsParentConsent) {
            return true
          }
          // Show this step if parent consent was requested but not completed
          return parentConsentRequestedAt == null || userParentConsentSatisfied
        },
        formInputs: [],
        ContainerComponent({ title: _, children }) {
          return (
            <>
              <HeaderComponent />
              <Heading size="xl" mb="32px">
                Secure your registration for the next {reservationTimeHours} hours
              </Heading>
              <Text mb="12px">
                Your parent has not completed the consent form yet.{' '}
                <chakra.span fontWeight="bold" color="fg.error">
                  If your parent does not complete this form within {reservationTimeHours} hours,
                  your reservation will expire.
                </chakra.span>
              </Text>
              {parentConsentRequestedAt != null ? (
                <Text mb="12px">
                  We&apos;ve sent the consent form to <b>{parentEmail}</b>. If you made a mistake,
                  you can{' '}
                  <ShwLink href={getRequestParentConsentUrl()} fontSize="normal" isExternal>
                    request consent again
                  </ShwLink>
                  .
                </Text>
              ) : null}
              <Box mt="24px">{children}</Box>
            </>
          )
        },
      },
      {
        slug: CAW_CONSENT_SLUG,
        title: 'Complete consent form',
        hide: () =>
          programSlug !== COLLEGE_ADMISSIONS_WORKSHOPS_PROGRAM_SLUG || completedCAWLearnerConsent,
        formInputs: getCAWConsentFormInputs({ type: 'learner' }),
        ContainerComponent({ title, children }) {
          return (
            <>
              <HeaderComponent />
              <Heading size="xl" as="h4" mb="32px">
                {title}
              </Heading>
              {children}
            </>
          )
        },
      },
      {
        slug: ALGEBRA2_LEARNING_LABS_CONSENT_SLUG,
        title: 'Complete consent form',
        hide: () =>
          programSlug !== ALGEBRA2_LEARNING_LABS_PROGRAM_SLUG ||
          completedAlgebra2LearningLabsLearnerConsent,
        formInputs: getLearningLabsConsentFormInputs(),
        ContainerComponent({ title, children }) {
          return (
            <>
              <HeaderComponent />
              <Heading size="xl" as="h4" mb="32px">
                {title}
              </Heading>
              {children}
            </>
          )
        },
      },
      {
        slug: PRE_CALCULUS_LEARNING_LABS_CONSENT_SLUG,
        title: 'Complete consent form',
        hide: () =>
          programSlug !== PRE_CALCULUS_LEARNING_LABS_PROGRAM_SLUG ||
          completedPreCalculusLearningLabsLearnerConsent,
        formInputs: getLearningLabsConsentFormInputs(),
        ContainerComponent({ title, children }) {
          return (
            <>
              <HeaderComponent />
              <Heading size="xl" as="h4" mb="32px">
                {title}
              </Heading>
              {children}
            </>
          )
        },
      },
      {
        slug: ALGEBRA2_FINALS_PREP_PROGRAM_STRUCTURE_CONSENT_SLUG,
        title: 'Program Structure',
        hide: () =>
          templateSlug == null ||
          !templateSlug.startsWith(ALGEBRA2_FINAL_EXAM_PREP_SPRING_2025_PREFIX),
        formInputs: getAlgebra2FinalsPrepProgramStructureConsentFormInputs(),
        ContainerComponent({ title, children }) {
          return (
            <>
              <HeaderComponent />
              <Heading size="xl" as="h4" mb="32px">
                {title}
              </Heading>
              {children}
            </>
          )
        },
      },
      {
        slug: 'sat-bootcamp-questions',
        title: 'Additional Information',
        hide: () => programSlug !== SAT_BOOTCAMP_PROGRAM_SLUG,
        formInputs: getSATBootcampLearnerFormInputs(testsBoundary),
        ContainerComponent({ title, children }) {
          return (
            <>
              <HeaderComponent />
              <Heading size="xl" as="h4" mb="32px">
                {title}
              </Heading>
              {children}
            </>
          )
        },
      },
      {
        slug: 'age-assurance',
        title: 'Almost done! One quick last step.',
        hide: () => hideAgeAssurance,
        formInputs: [
          {
            type: 'content',
            name: 'age-assurance-content',
            display: 'display',
            content: (
              <Center>
                <AgeAssuranceGuideWithAutoSubmit
                  onSessionModalDone={() => {
                    setShouldSubmit(true)
                  }}
                  onAgeAssured={() => {
                    setIsAgeAssuranceComplete(true)
                  }}
                />
              </Center>
            ),
            size: 6,
          },
        ],
        NextButtonComponent({ children, ...buttonProps }) {
          return (
            <Button {...buttonProps} variant={shouldSubmit ? undefined : 'outline'}>
              {!isAgeAssuranceComplete ? 'Verify Later' : children}
            </Button>
          )
        },
        ContainerComponent({ title, children }) {
          return (
            <>
              <HeaderComponent />
              <Heading size="xl" as="h4" mb="32px">
                {title}
              </Heading>
              {children}
            </>
          )
        },
      },
    ],
    [
      attendancePolicy,
      seriesQuestionnaire,
      hasRequiredAttendanceSessions,
      isSATBootcampCourse,
      HeaderComponent,
      testsBoundary,
      tutorInfo,
      requirePhoneNumber,
      hasPhoneNumber,
      hasPhoneNumberWaived,
      parentConsentRequestedAt,
      userParentConsentSatisfied,
      parentEmail,
      reservationTimeHours,
      userAssentCompletedAt,
      programSlug,
      completedCAWLearnerConsent,
      completedAlgebra2LearningLabsLearnerConsent,
      completedPreCalculusLearningLabsLearnerConsent,
      templateSlug,
      programNeedsParentConsent,
      programNeedsAssent,
      userHasParentContactInfo,
      currentUser,
      shouldSubmit,
      hideAgeAssurance,
      isAgeAssuranceComplete,
      setIsAgeAssuranceComplete,
    ]
  )

  const [handleSubmit] = useAPIWrapper(
    async (values: FormWizardV2Values<SeriesRegistrationWizardStepNames>) => {
      if (seriesId == null) {
        // Handling null case because of query builder typing
        return
      }

      trackEvent({
        category: 'Registration',
        action: 'submitting',
      })

      if (isSATBootcampCourse) {
        await RPC.createBasicSATUserFromInput({
          userId: currentUser.id,
          type: 'learner',
        })
      }

      if (values['request-parent-contact'] != null) {
        trackEvent({
          category: 'Registration',
          action: 'submitting inline-parent-contact',
        })

        const formattedPhone = `${values['request-parent-contact'].parent_phone}`.startsWith('+')
          ? `${values['request-parent-contact'].parent_phone}`
          : `+${values['request-parent-contact'].parent_phone}`

        await RPC.upsertParentContact({
          parentFirstName: `${values['request-parent-contact'].parent_first_name}`,
          parentLastName: `${values['request-parent-contact'].parent_last_name}`,
          parentEmail: `${values['request-parent-contact'].parent_email}`,
          parentPhone: formattedPhone,
        })
      }

      if (values['request-parent-consent'] != null) {
        trackEvent({
          category: 'Registration',
          action: 'submitting inline-parent-consent',
        })

        const formattedPhone = `${values['request-parent-consent'].parent_phone}`.startsWith('+')
          ? `${values['request-parent-consent'].parent_phone}`
          : `+${values['request-parent-consent'].parent_phone}`

        await RPC.requestParentConsent({
          parentFirstName: `${values['request-parent-consent'].parent_first_name}`,
          parentLastName: `${values['request-parent-consent'].parent_last_name}`,
          parentEmail: `${values['request-parent-consent'].parent_email}`,
          parentPhone: formattedPhone,
        })
      }
      if (values['request-student-assent'] != null) {
        trackEvent({
          category: 'Registration',
          action: 'submitting inline-student-assent',
        })

        // eslint-disable-next-line local/no-new-type-assertions
        const agreementArray = values['request-student-assent'].assent_agreement as string[]
        const agreed = agreementArray?.includes('agreed')

        if (!agreed) {
          throw new Error('Agreement is required to participate')
        }

        await RPC.submitAssent({})
      }

      const reserveForEmails: string[] = []
      const reserveForCount = 0

      const shouldReserve = programNeedsParentConsent && !userParentConsentSatisfied

      const seriesAction = shouldReserve ? 'reserve' : 'register'

      if (seriesAction === 'reserve' && isSATBootcampCourse) {
        trackEvent({
          category: 'Registration',
          action: 'reserving for self',
        })
      } else if (isSATBootcampCourse) {
        trackEvent({
          category: 'Registration',
          action: 'registering for series',
        })
      }

      const phoneNumber = values['phone-number']?.phoneNumber
      if (phoneNumber != null) {
        await RPC.updatePhoneNumberWithoutVerification({
          phoneNumber: `+${phoneNumber}`,
          failSilently: true,
        })
      }

      const cawConsent = values[CAW_CONSENT_SLUG]
      if (cawConsent != null) {
        const responses = getCAWConsentResponses({ formValues: cawConsent })
        await RPC.submitProgramConsent({
          type: 'learner',
          worldSlug: COLLEGE_ADMISSIONS_WORKSHOPS_WORLD_SLUG,
          responses,
        })
      }

      const algebra2LearningLabsConsent = values[ALGEBRA2_LEARNING_LABS_CONSENT_SLUG]
      if (algebra2LearningLabsConsent != null) {
        const responses = getLearningLabsConsentResponses(algebra2LearningLabsConsent)
        await RPC.submitProgramConsent({
          type: 'learner',
          worldSlug: ALGEBRA2_LEARNING_LABS_WORLD_SLUG,
          responses,
        })
      }

      const preCalculusLearningLabsConsent = values[PRE_CALCULUS_LEARNING_LABS_CONSENT_SLUG]
      if (preCalculusLearningLabsConsent != null) {
        const responses = getLearningLabsConsentResponses(preCalculusLearningLabsConsent)
        await RPC.submitProgramConsent({
          type: 'learner',
          worldSlug: PRE_CALCULUS_LEARNING_LABS_WORLD_SLUG,
          responses,
        })
      }

      await pMap(
        seriesIdsToUnregister,
        async seriesIdToUnregister => {
          assertDefined(
            switchReason,
            'switchReason is required when seriesIdsToUnregister is non-empty'
          )
          await RPC.unregisterSeries({
            seriesId: seriesIdToUnregister,
            withdrawalReason: `Switched: ${switchReason}`,
            withdrawalSource: SeriesWithdrawalSource.Learner,
            bootcampNPS: null,
          })
        },
        { concurrency: 5 }
      )

      await pMap(
        seriesIdsToUnreserve,
        async seriesIdToUnreserve => {
          await RPC.cancelSeriesReservation({
            seriesId: seriesIdToUnreserve,
          })
        },
        { concurrency: 5 }
      )

      const details = values['attendance-policy-and-questionnaire'] as FormValues

      const { 'enable-text': enableTextCheckbox, ...restOfDetails } = details

      const registerOrReserveResult = await RPCRefresh.registerOrReserveSeries({
        seriesId,
        details: restOfDetails,
        reserveForEmails,
        reserveForCount,
        source,
      })

      if (!registerOrReserveResult.success) {
        const { moderationResult } = registerOrReserveResult
        if (moderationResult.status === 'blocked') {
          setBlockedError(moderationResult.message)
        }
        if (moderationResult.status === 'suspended') {
          router.reload()
        }
        return
      }

      const { result } = registerOrReserveResult

      if (result === 'reserve') {
        toast({
          title: 'You have successfully reserved your spot in this series!',
          status: 'success',
        })
      } else {
        toast({
          title: 'You have successfully registered for this series!',
          status: 'success',
        })
      }
      onClose?.()

      const satBootcampQuestions = values['sat-bootcamp-questions']
      if (satBootcampQuestions != null && isSATBootcampCourse) {
        await RPC.updateAssociatedSATUser({
          internalState: {
            test_schedule: satBootcampQuestions.test_schedule,
            guidance_counselor_email: satBootcampQuestions.counselor_email,
          },
        })
      }

      await safeRouterPush({ router, url: redirectUrl, reloadAfterRedirect })

      if (Array.isArray(enableTextCheckbox) && enableTextCheckbox[0] === 'yes') {
        showPhoneNumberModal({})
      }
    },
    [
      currentUser.id,
      isSATBootcampCourse,
      programNeedsParentConsent,
      userParentConsentSatisfied,
      onClose,
      redirectUrl,
      reloadAfterRedirect,
      router,
      seriesId,
      seriesIdsToUnregister,
      seriesIdsToUnreserve,
      showPhoneNumberModal,
      source,
      switchReason,
      toast,
    ],
    'Register or reserve series'
  )

  const steps = allSteps.filter(item => item.hide == null || !item.hide())

  return { content, steps, handleSubmit, blockedError }
}
